///////////////////////////////////////
// Nested Resource Mixin
//
// This mixin is intended to support nested api resources. It works by inspecting
// the adapterOptions object passed to any of the Ember.Store's find/update/delete
// methods and looking for a defined array of model/id pairs.  If the array is found,
// the model/id pairs are used in succession to construct a nested url for the desired
// resource.
//
// Ex:
//  store.findRecord('assignment', 43, {
//    adapterOptions: {
//      pathPrefixModelIdPairs: ['user:48', 'pony:37', 'application:99']
//    }
//  });`
//
// will yield a GET call to...
//
// https://api.storyhunterlocal.com/users/48/ponies/37/applications/99/assignments/43
//
// ---
// The mixin's primary strategy is to override the adapter methods found in Build-URL-Mixin
//
// Build-URL-Mixin: https://github.com/emberjs/data/blob/v2.14.10/addon/-private/adapters/build-url-mixin.js
///////////////////////////////////////


import Mixin from '@ember/object/mixin';

export default Mixin.create({
  _checkForPathPrefix(snapshot) {
    let path = '';
    if (snapshot && snapshot.adapterOptions && snapshot.adapterOptions.pathPrefixModelIdPairs) {
      let pathPrefixModelIdPairs = snapshot.adapterOptions.pathPrefixModelIdPairs;

      try {
        if (!Array.isArray(pathPrefixModelIdPairs)) {
          throw 'NestedResourceMixin Error: pathPrefixModelIdPairs must be an array!';
        }
      } catch (e){
        console.error(e);
        return path;
      }

      path = pathPrefixModelIdPairs.reduce((prevVal, item, index) => {
        let splits = item.split(':');

        try {
          if (splits.length !== 2) {
            throw 'NestedResourceMixin Error: Model/Id pair string formatted incorrectly! Should be of the form \'modelName:id\'';
          }
        } catch (e) {
          console.error(e);
          return prevVal;
        }

        let modelName = splits[0];
        let id = splits[1];

        prevVal += `${this.pathForType(modelName)}/${id}`;
        return (pathPrefixModelIdPairs.length - 1 === index) ? prevVal : prevVal + '/';
      }, '');
    }
    return path;
  },

  /**
   * Check to see if we shoudl include include params in the query string.
   *
   * @param {Object} snapshot
   * @returns {string}    include params URIEncoded
   * @private
   */
  _checkForIncludeParams(snapshot) {
    if (snapshot && snapshot.adapterOptions && snapshot.adapterOptions.include) {
      return `include=${encodeURIComponent(snapshot.adapterOptions.include)}`;
    }

    return '';
  },

  /**
   * Check if we need to include a public prefix for this endpoint.
   *
   * @param {Object} snapshot
   * @return {string}
   * @private
   */
  _checkForPublicPrefix(snapshot) {
    if (snapshot && snapshot.adapterOptions && snapshot.adapterOptions.isPublic) {
      return this.get('session.isAuthenticated') ? '' : 'public';
    }

    return '';
  },

  urlForFindRecord(id, modelName, snapshot) {
    // This is the default behavior! (https://github.com/emberjs/data/blob/v2.14.10/addon/-private/adapters/build-url-mixin.js#L126)
    // return this._buildURL(modelName, id);

    let pathPrefix = this._checkForPathPrefix(snapshot);
    pathPrefix = this._checkForPublicPrefix(snapshot) + pathPrefix;
    let includeParams = this._checkForIncludeParams(snapshot);
    modelName = pathPrefix ? `${pathPrefix}/${modelName}` : modelName;

    let path = this._super(id, modelName, snapshot);
    return includeParams ? `${path}?${includeParams}` : path;
  },

  urlForFindAll(modelName, snapshot) {
    // This is the default behavior! (https://github.com/emberjs/data/blob/v2.14.10/addon/-private/adapters/build-url-mixin.js#L152)
    //return this._buildURL(modelName);

    let pathPrefix = this._checkForPathPrefix(snapshot);
    pathPrefix = this._checkForPublicPrefix(snapshot) + pathPrefix;
    let includeParams = this._checkForIncludeParams(snapshot);
    modelName = pathPrefix ? `${pathPrefix}/${modelName}` : modelName;

    let path = this._super(modelName, snapshot);
    return includeParams ? `${path}?${includeParams}` : path;
  },

  // ---
  // Note:
  //
  // Ember doesn't pass a snapshot to the query methods, so there's no way to support nested query endpoints using the
  // adapterOptions pattern.  This shouldn't be an issue however as we'd likely be able to pass any filtering params
  // we need as part of the query itself.  So, we currently do not need to override these methods,
  // but I'm leaving them here for consistency with the original build-url-mixin from Ember-Data.  If we ever
  // need to adjust the store.query() behavior, this is the place to do it.
  // ---

  urlForQuery(query, modelName) {
    // This is the default behavior! (https://github.com/emberjs/data/blob/v2.14.10/addon/-private/adapters/build-url-mixin.js#L182)
    //return this._buildURL(modelName);

    let pathPrefix = this._checkForPathPrefix(query);
    modelName = pathPrefix ? `${pathPrefix}/${modelName}` : modelName;

    if (query.adapterOptions) {
      delete query.adapterOptions;
    }

    return this._super(query, modelName);
  },
  //
  urlForQueryRecord(query, modelName) {
    // This is the default behavior! (https://github.com/emberjs/data/blob/v2.14.10/addon/-private/adapters/build-url-mixin.js#L207)
    //return this._buildURL(modelName);

    let pathPrefix = this._checkForPathPrefix(query);
    modelName = pathPrefix ? `${pathPrefix}/${modelName}` : modelName;

    if (query.adapterOptions) {
      delete query.adapterOptions;
    }

    return this._super(query, modelName);
  },

  urlForFindMany(id, modelName, snapshot) {
    // This is the default behavior! (https://github.com/emberjs/data/blob/v2.14.10/addon/-private/adapters/build-url-mixin.js#L234)
    // return this._buildURL(modelName);

    let pathPrefix = this._checkForPathPrefix(snapshot);
    modelName = pathPrefix ? `${pathPrefix}/${modelName}` : modelName;
    return this._super(id, modelName, snapshot);
  },

  urlForFindHasMany(id, modelName, snapshot) {
    // This is the default behavior! (https://github.com/emberjs/data/blob/v2.14.10/addon/-private/adapters/build-url-mixin.js#L261)
    // return this._buildURL(modelName, id);

    let pathPrefix = this._checkForPathPrefix(snapshot);
    modelName = pathPrefix ? `${pathPrefix}/${modelName}` : modelName;
    return this._super(id, modelName, snapshot);
  },

  urlForFindBelongsTo(id, modelName, snapshot) {
    // This is the default behavior! (https://github.com/emberjs/data/blob/v2.14.10/addon/-private/adapters/build-url-mixin.js#L288)
    // return this._buildURL(modelName, id);

    let pathPrefix = this._checkForPathPrefix(snapshot);
    modelName = pathPrefix ? `${pathPrefix}/${modelName}` : modelName;
    return this._super(id, modelName, snapshot);
  },

  urlForCreateRecord(modelName, snapshot) {
    // This is the default behavior! (https://github.com/emberjs/data/blob/v2.14.10/addon/-private/adapters/build-url-mixin.js#L314)
    // return this._buildURL(modelName);

    let pathPrefix = this._checkForPathPrefix(snapshot);
    pathPrefix = this._checkForPublicPrefix(snapshot) + pathPrefix;
    let includeParams = this._checkForIncludeParams(snapshot);
    modelName = pathPrefix ? `${pathPrefix}/${modelName}` : modelName;

    let path = this._super(modelName, snapshot);
    return includeParams ? `${path}?${includeParams}` : path;
  },

  urlForUpdateRecord(id, modelName, snapshot) {
    // This is the default behavior! (https://github.com/emberjs/data/blob/v2.14.10/addon/-private/adapters/build-url-mixin.js#L338)
    // return this._buildURL(modelName, id);

    let pathPrefix = this._checkForPathPrefix(snapshot);
    pathPrefix = this._checkForPublicPrefix(snapshot) + pathPrefix;
    let includeParams = this._checkForIncludeParams(snapshot);
    modelName = pathPrefix ? `${pathPrefix}/${modelName}` : modelName;

    let path = this._super(id, modelName, snapshot);
    return includeParams ? `${path}?${includeParams}` : path;
  },

  urlForDeleteRecord(id, modelName, snapshot) {
    // This is the default behavior! (https://github.com/emberjs/data/blob/v2.14.10/addon/-private/adapters/build-url-mixin.js#L363)
    // return this._buildURL(modelName, id);

    let pathPrefix = this._checkForPathPrefix(snapshot);
    pathPrefix = this._checkForPublicPrefix(snapshot) + pathPrefix;
    modelName = pathPrefix ? `${pathPrefix}/${modelName}` : modelName;
    return this._super(id, modelName, snapshot);
  }

});
